import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { cancelPortvilleLottery } from '../../../services/PortvilleContractCancellationService';
import { downloadStorageFile } from '../../../utils/storageDownload';

type LotteryStatus = 'drawn' | 'contracted' | 'cancelled' | string;
type CancellationRequestStatus = 'pending' | 'rejected' | 'approved' | 'cancelled' | string;

interface PortvilleLotteryRecord {
  id: string;
  uid: string;
  roomId?: string;
  roomNumber?: string;
  status: LotteryStatus;
  drawnAt?: Date | null;
  contractedAt?: Date | null;
  archivedAt?: Date | null;
  archivedReason?: string;
  suppressedAt?: Date | null;
  suppressedReason?: string;
  linkedResidencyId?: string;
  cancellationRequestStatus?: CancellationRequestStatus;
  cancellationReason?: string;
  cancellationRequestedAt?: Date | null;
  cancellationApprovedAt?: Date | null;
  cancellationReviewedAt?: Date | null;
  cancellationReviewMemo?: string;
  cancelledAt?: Date | null;
  adminCancellationAttachment?: CancellationAttachment;
  userName?: string;
  userPhone?: string;
  userOrganization?: string;
  userEmail?: string;
}

interface CancellationAttachment {
  name: string;
  storagePath: string;
  size: number;
  contentType: string;
  uploadedAt?: Date | null;
  uploadedByUid?: string;
}

const MAX_CANCELLATION_ATTACHMENT_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tif', 'tiff'];

const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() || '';

const getCancellationAttachmentContentType = (file: File) => {
  if (file.type === 'application/pdf') return file.type;
  if (file.type.startsWith('image/')) return file.type;

  const extension = getFileExtension(file.name);
  const fallbackTypes: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    heic: 'image/heic',
    heif: 'image/heif',
    tif: 'image/tiff',
    tiff: 'image/tiff'
  };
  return fallbackTypes[extension] || '';
};

const validateCancellationAttachment = (file: File) => {
  const extension = getFileExtension(file.name);
  const contentType = getCancellationAttachmentContentType(file);
  const isPdf = extension === 'pdf' && contentType === 'application/pdf';
  const isImage = ALLOWED_IMAGE_EXTENSIONS.includes(extension) && contentType.startsWith('image/');

  if (!isPdf && !isImage) return 'PDF 또는 이미지 파일만 첨부할 수 있습니다.';
  if (file.size > MAX_CANCELLATION_ATTACHMENT_SIZE) return '첨부파일은 5MB 이하만 업로드할 수 있습니다.';
  if (file.size === 0) return '빈 파일은 첨부할 수 없습니다.';
  return '';
};

const sanitizeFileName = (fileName: string) => fileName.replace(/[^a-zA-Z0-9._-]/g, '_');

type FilterValue =
  | 'all'
  | 'drawn'
  | 'contracted'
  | 'cancelled'
  | 'pendingCancellation'
  | 'rejectedCancellation'
  | 'suppressed';

const filterOptions: Array<{ value: FilterValue; label: string }> = [
  { value: 'all', label: '전체' },
  { value: 'drawn', label: '계약 준비' },
  { value: 'pendingCancellation', label: '포기신청' },
  { value: 'rejectedCancellation', label: '포기반려' },
  { value: 'suppressed', label: '입주점검 이후' },
  { value: 'contracted', label: '계약완료' },
  { value: 'cancelled', label: '포기/취소' }
];

const toDate = (value: any): Date | null => {
  try {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const formatDateTime = (date?: Date | null) => {
  if (!date) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizeText = (value?: string | number | null) => String(value ?? '').toLowerCase();

const getResultKey = (record: PortvilleLotteryRecord): Exclude<FilterValue, 'all'> | 'unknown' => {
  if (record.status === 'cancelled' || record.cancelledAt || record.archivedReason === 'cancelled') {
    return 'cancelled';
  }
  if (record.status === 'contracted' || record.contractedAt) {
    return 'contracted';
  }
  if (record.suppressedAt) {
    return 'suppressed';
  }
  if (record.cancellationRequestStatus === 'pending') {
    return 'pendingCancellation';
  }
  if (record.cancellationRequestStatus === 'rejected') {
    return 'rejectedCancellation';
  }
  if (record.status === 'drawn') {
    return 'drawn';
  }
  return 'unknown';
};

const getResultLabel = (record: PortvilleLotteryRecord) => {
  const resultKey = getResultKey(record);
  if (resultKey === 'cancelled') return { label: '포기/취소 완료', className: 'bg-gray-200 text-gray-800' };
  if (resultKey === 'contracted') return { label: '계약 완료', className: 'bg-green-100 text-green-800' };
  if (resultKey === 'suppressed') return { label: '입주점검 이후', className: 'bg-blue-100 text-blue-800' };
  if (resultKey === 'pendingCancellation') return { label: '포기신청 검토중', className: 'bg-red-100 text-red-800' };
  if (resultKey === 'rejectedCancellation') return { label: '포기신청 반려', className: 'bg-yellow-100 text-yellow-800' };
  if (resultKey === 'drawn') return { label: '계약 준비', className: 'bg-indigo-100 text-indigo-800' };
  return { label: record.status || '상태 미확인', className: 'bg-gray-100 text-gray-700' };
};

const getStatusLabel = (status: LotteryStatus) => {
  switch (status) {
    case 'drawn':
      return '추첨완료';
    case 'contracted':
      return '계약완료';
    case 'cancelled':
      return '취소';
    default:
      return status || '-';
  }
};

const PortvilleLotteryManagePage: React.FC = () => {
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const [records, setRecords] = useState<PortvilleLotteryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterValue>('all');
  const [processingLotteryId, setProcessingLotteryId] = useState<string | null>(null);
  const [cancellationTarget, setCancellationTarget] = useState<PortvilleLotteryRecord | null>(null);
  const [cancellationAttachment, setCancellationAttachment] = useState<File | null>(null);
  const [cancellationAttachmentError, setCancellationAttachmentError] = useState('');
  const [downloadingAttachmentPath, setDownloadingAttachmentPath] = useState<string | null>(null);

  const loadLotteryRecords = async () => {
    try {
      setLoading(true);
      setError(null);

      const snapshot = await getDocs(query(collection(db, 'portville_lottery_history'), orderBy('drawnAt', 'desc')));
      const baseRecords: PortvilleLotteryRecord[] = snapshot.docs.map((lotteryDoc) => {
        const data = lotteryDoc.data();
        return {
          id: lotteryDoc.id,
          uid: data.uid || '',
          roomId: data.roomId || '',
          roomNumber: data.roomNumber ? String(data.roomNumber) : '',
          status: data.status || '',
          drawnAt: toDate(data.drawnAt),
          contractedAt: toDate(data.contractedAt),
          archivedAt: toDate(data.archivedAt),
          archivedReason: data.archivedReason || '',
          suppressedAt: toDate(data.suppressedAt),
          suppressedReason: data.suppressedReason || '',
          linkedResidencyId: data.linkedResidencyId || '',
          cancellationRequestStatus: data.cancellationRequestStatus || '',
          cancellationReason: data.cancellationReason || '',
          cancellationRequestedAt: toDate(data.cancellationRequestedAt),
          cancellationApprovedAt: toDate(data.cancellationApprovedAt),
          cancellationReviewedAt: toDate(data.cancellationReviewedAt),
          cancellationReviewMemo: data.cancellationReviewMemo || '',
          cancelledAt: toDate(data.cancelledAt),
          adminCancellationAttachment: data.adminCancellationAttachment
            ? {
              ...data.adminCancellationAttachment,
              uploadedAt: toDate(data.adminCancellationAttachment.uploadedAt)
            }
            : undefined
        };
      });

      const uniqueUids = Array.from(new Set(baseRecords.map((record) => record.uid).filter(Boolean)));
      const profileEntries = await Promise.all(
        uniqueUids.map(async (uid) => {
          try {
            const profileSnap = await getDoc(doc(db, 'user_profiles', uid));
            if (!profileSnap.exists()) return [uid, null] as const;
            return [uid, profileSnap.data()] as const;
          } catch (profileError) {
            console.warn('추첨 기록 사용자 프로필 조회 실패:', uid, profileError);
            return [uid, null] as const;
          }
        })
      );
      const profileMap = new Map(profileEntries);

      setRecords(
        baseRecords.map((record) => {
          const profile = profileMap.get(record.uid) as any;
          return {
            ...record,
            userName: profile?.name || '',
            userPhone: profile?.phoneNumber || '',
            userOrganization: profile?.organization || '',
            userEmail: profile?.email || ''
          };
        })
      );
    } catch (loadError) {
      console.error('포트빌 추첨 기록 조회 실패:', loadError);
      setError('포트빌 추첨 기록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && hasAccess) {
      loadLotteryRecords();
    }
  }, [authLoading, hasAccess]);

  const stats = useMemo(() => {
    return records.reduce(
      (acc, record) => {
        const resultKey = getResultKey(record);
        acc.all += 1;
        if (resultKey !== 'unknown') acc[resultKey] += 1;
        return acc;
      },
      {
        all: 0,
        drawn: 0,
        contracted: 0,
        cancelled: 0,
        pendingCancellation: 0,
        rejectedCancellation: 0,
        suppressed: 0
      }
    );
  }, [records]);

  const filteredRecords = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    return records.filter((record) => {
      const resultKey = getResultKey(record);
      const matchesFilter =
        filter === 'all' ||
        resultKey === filter;

      if (!matchesFilter) return false;
      if (!keyword) return true;

      return [
        record.roomNumber,
        record.userName,
        record.userPhone,
        record.userOrganization,
        record.userEmail,
        record.uid,
        record.cancellationReason
      ].some((value) => normalizeText(value).includes(keyword));
    });
  }, [filter, records, searchTerm]);

  const openCancellationModal = (record: PortvilleLotteryRecord) => {
    if (getResultKey(record) !== 'drawn' || record.status !== 'drawn') {
      alert('추첨완료/계약 준비 상태의 기록만 포기 처리할 수 있습니다.');
      return;
    }

    setCancellationTarget(record);
    setCancellationAttachment(null);
    setCancellationAttachmentError('');
  };

  const closeCancellationModal = () => {
    if (processingLotteryId) return;
    setCancellationTarget(null);
    setCancellationAttachment(null);
    setCancellationAttachmentError('');
  };

  const handleCancellationAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;

    const validationError = validateCancellationAttachment(file);
    if (validationError) {
      setCancellationAttachment(null);
      setCancellationAttachmentError(validationError);
      return;
    }

    setCancellationAttachment(file);
    setCancellationAttachmentError('');
  };

  const uploadCancellationAttachment = async (lotteryId: string, file: File) => {
    const attachmentId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const storagePath = `portville_lottery_cancellations/${lotteryId}/${attachmentId}_${sanitizeFileName(file.name)}`;
    const contentType = getCancellationAttachmentContentType(file);
    await uploadBytes(ref(storage, storagePath), file, { contentType });
    return { name: file.name, storagePath, size: file.size, contentType };
  };

  const handleConfirmCancellation = async () => {
    const record = cancellationTarget;
    if (!record || processingLotteryId) return;

    if (cancellationAttachment) {
      const validationError = validateCancellationAttachment(cancellationAttachment);
      if (validationError) {
        setCancellationAttachmentError(validationError);
        return;
      }
    }

    let uploadedAttachment: Awaited<ReturnType<typeof uploadCancellationAttachment>> | undefined;

    try {
      setProcessingLotteryId(record.id);
      setError(null);
      if (cancellationAttachment) {
        uploadedAttachment = await uploadCancellationAttachment(record.id, cancellationAttachment);
      }
      await cancelPortvilleLottery({
        lotteryId: record.id,
        ...(uploadedAttachment ? { attachment: uploadedAttachment } : {})
      });
      setCancellationTarget(null);
      setCancellationAttachment(null);
      setCancellationAttachmentError('');
      await loadLotteryRecords();
      alert('추첨 기록이 포기/취소 처리되었습니다.');
    } catch (cancelError: any) {
      console.error('포트빌 추첨 기록 포기 처리 실패:', cancelError);
      let committedDespiteError = false;

      if (uploadedAttachment) {
        try {
          const latestLottery = await getDoc(doc(db, 'portville_lottery_history', record.id));
          const latestData = latestLottery.data();
          committedDespiteError = latestData?.status === 'cancelled'
            && latestData?.adminCancellationAttachment?.storagePath === uploadedAttachment.storagePath;

          if (!committedDespiteError) {
            await deleteObject(ref(storage, uploadedAttachment.storagePath));
          }
        } catch (cleanupError) {
          console.error('포기 처리 실패 후 첨부파일 상태 확인/정리 실패:', cleanupError);
        }
      }

      if (committedDespiteError) {
        setCancellationTarget(null);
        setCancellationAttachment(null);
        setCancellationAttachmentError('');
        await loadLotteryRecords();
        alert('추첨 기록이 포기/취소 처리되었습니다.');
      } else {
        setError(cancelError?.message || '추첨 기록을 포기 처리하지 못했습니다.');
      }
    } finally {
      setProcessingLotteryId(null);
    }
  };

  const handleDownloadCancellationAttachment = async (attachment: CancellationAttachment) => {
    try {
      setDownloadingAttachmentPath(attachment.storagePath);
      const fileNameWithoutExtension = attachment.name.replace(/\.[^/.]+$/, '') || '포기처리_첨부파일';
      await downloadStorageFile(attachment.storagePath, fileNameWithoutExtension);
    } catch (downloadError) {
      console.error('포기 처리 첨부파일 다운로드 실패:', downloadError);
      alert('첨부파일을 다운로드하지 못했습니다.');
    } finally {
      setDownloadingAttachmentPath(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">포트빌 추첨 기록을 불러오는 중...</div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1e2c49]">포트빌 추첨 관리</h1>
        <p className="text-gray-600 mt-1">포트빌 호실 추첨 기록과 계약 진행 결과를 확인합니다.</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={loadLotteryRecords}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              다시 시도
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {filterOptions.map((option) => {
          const count = stats[option.value] ?? 0;
          const active = filter === option.value;
          return (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`rounded-lg border p-4 text-left transition-colors ${
                active
                  ? 'bg-[#4b8fe4] border-[#4b8fe4] text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="text-sm font-medium">{option.label}</div>
              <div className="mt-1 text-2xl font-bold">{count}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">추첨 기록 목록</h2>
            <p className="text-sm text-gray-500 mt-1">총 {filteredRecords.length}건 표시</p>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="호실, 이름, 연락처, 회사명 검색"
              className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4b8fe4]"
            />
            <button
              onClick={loadLotteryRecords}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              새로고침
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">추첨일</th>
                <th className="w-20 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">호실</th>
                <th className="w-40 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">신청자</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">추첨 상태</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">처리 결과</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">포기신청</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">연결 정보</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">관리</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    표시할 추첨 기록이 없습니다.
                  </td>
                </tr>
              ) : (
                filteredRecords.map((record) => {
                  const result = getResultLabel(record);
                  const canCancelLottery = getResultKey(record) === 'drawn' && record.status === 'drawn';
                  const isProcessing = processingLotteryId === record.id;
                  return (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(record.drawnAt)}
                      </td>
                      <td className="w-20 px-3 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {record.roomNumber ? `${record.roomNumber}호` : '-'}
                        </div>
                      </td>
                      <td className="w-40 max-w-40 px-3 py-4">
                        <div className="truncate text-sm font-medium text-gray-900" title={record.userName || undefined}>{record.userName || '-'}</div>
                        <div className="truncate text-sm text-gray-600" title={record.userPhone || undefined}>{record.userPhone || '-'}</div>
                        <div
                          className="truncate text-xs text-gray-500"
                          title={record.userOrganization || record.userEmail || record.uid}
                        >
                          {record.userOrganization || record.userEmail || record.uid}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          {getStatusLabel(record.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${result.className}`}>
                          {result.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 min-w-[220px] text-sm text-gray-700">
                        <div className="space-y-1">
                          {record.cancellationRequestStatus ? (
                            <>
                            <div>
                              상태: {record.cancellationRequestStatus === 'pending' ? '검토중' : record.cancellationRequestStatus === 'rejected' ? '반려' : record.cancellationRequestStatus === 'approved' ? '승인' : record.cancellationRequestStatus}
                            </div>
                            {record.cancellationRequestedAt && <div>신청: {formatDateTime(record.cancellationRequestedAt)}</div>}
                            {record.cancellationApprovedAt && <div>승인: {formatDateTime(record.cancellationApprovedAt)}</div>}
                            {record.cancellationReviewedAt && <div>검토: {formatDateTime(record.cancellationReviewedAt)}</div>}
                            {record.cancellationReason && <div className="text-gray-500 line-clamp-2">사유: {record.cancellationReason}</div>}
                            </>
                          ) : !record.adminCancellationAttachment ? (
                            <span className="text-gray-400">없음</span>
                          ) : null}
                          {record.adminCancellationAttachment && (
                            <button
                              type="button"
                              onClick={() => handleDownloadCancellationAttachment(record.adminCancellationAttachment!)}
                              disabled={downloadingAttachmentPath === record.adminCancellationAttachment.storagePath}
                              className="block max-w-[260px] truncate text-left text-blue-600 hover:text-blue-800 hover:underline disabled:text-gray-400"
                              title={record.adminCancellationAttachment.name}
                            >
                              첨부: {record.adminCancellationAttachment.name}
                              {downloadingAttachmentPath === record.adminCancellationAttachment.storagePath ? ' (다운로드 중...)' : ''}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 min-w-[180px] text-sm text-gray-700">
                        {record.contractedAt && <div>계약: {formatDateTime(record.contractedAt)}</div>}
                        {record.cancelledAt && <div>취소: {formatDateTime(record.cancelledAt)}</div>}
                        {record.suppressedAt && <div>점검 이후: {formatDateTime(record.suppressedAt)}</div>}
                        {record.linkedResidencyId && <div className="text-xs text-gray-500">입주기록: {record.linkedResidencyId}</div>}
                        {!record.contractedAt && !record.cancelledAt && !record.suppressedAt && !record.linkedResidencyId && (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {canCancelLottery ? (
                          <button
                            onClick={() => openCancellationModal(record)}
                            disabled={isProcessing}
                            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                              isProcessing
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            {isProcessing ? '처리 중...' : '포기 처리'}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {cancellationTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={closeCancellationModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancellation-modal-title"
            className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="cancellation-modal-title" className="text-xl font-bold text-gray-900">추첨 포기 처리</h2>
            <p className="mt-2 text-sm text-gray-600">
              {cancellationTarget.roomNumber || '-'}호 · {cancellationTarget.userName || cancellationTarget.uid}
            </p>

            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              확인하면 해당 호실은 공실로 전환됩니다. 이 작업은 되돌릴 수 없습니다.
            </div>

            <div className="mt-5">
              <label htmlFor="cancellation-attachment" className="block text-sm font-medium text-gray-700">
                첨부파일 <span className="font-normal text-gray-500">(선택사항, 최대 5MB)</span>
              </label>
              <input
                id="cancellation-attachment"
                type="file"
                accept="application/pdf,image/*"
                onChange={handleCancellationAttachmentChange}
                disabled={Boolean(processingLotteryId)}
                className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-gray-700"
              />
              <p className="mt-1 text-xs text-gray-500">PDF 또는 이미지 파일 1개만 등록할 수 있습니다.</p>

              {cancellationAttachment && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-800">{cancellationAttachment.name}</div>
                    <div className="text-xs text-gray-500">{(cancellationAttachment.size / 1024 / 1024).toFixed(2)}MB</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCancellationAttachment(null)}
                    disabled={Boolean(processingLotteryId)}
                    className="shrink-0 text-sm text-red-600 hover:text-red-800 disabled:text-gray-400"
                  >
                    제거
                  </button>
                </div>
              )}
              {cancellationAttachmentError && <p className="mt-2 text-sm text-red-600">{cancellationAttachmentError}</p>}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeCancellationModal}
                disabled={Boolean(processingLotteryId)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleConfirmCancellation}
                disabled={Boolean(processingLotteryId)}
                className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {processingLotteryId ? '처리 중...' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortvilleLotteryManagePage;
